import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { ChevronLeft } from "lucide-react";

/**
 * PageBar — the app-shell in-content page header block rendered by
 * DashboardLayoutFull (sits below the global strip + attention slot).
 *
 * Pages opt in by calling
 * `usePageBar({ title, crumb?, description?, meta?, actions? })`: the config
 * is registered on mount (and kept in sync on re-render) and removed on
 * unmount. When no page has registered anything the header renders NOTHING,
 * so non-migrated routes are unchanged.
 *
 * The block matches the approved shell mockup's `.phead`: an optional
 * back-crumb line above a large title, an optional muted description below,
 * and right-aligned page-level actions. The title always renders when a page
 * registers one — this is the wayfinding anchor when the sidebar is collapsed.
 *
 * Registration is a stack: if a child registers after its parent (e.g.
 * BillingPage inside Settings), the child's config wins until it unmounts.
 *
 * Two contexts on purpose: pages consume only the stable registry (so
 * registry updates never re-render pages), while the PageBar component alone
 * consumes the active config.
 */

export interface PageBarConfig {
  /** Page title — large, left-aligned */
  title: ReactNode;
  /**
   * Optional parent crumb for detail pages (e.g. a Link back to the list).
   * Rendered on its own line above the title with a back chevron.
   */
  crumb?: ReactNode;
  /** Optional muted description below the title (e.g. "Build, preview, and ship…"). */
  description?: ReactNode;
  /** Optional count/status after the title, rendered as a subtle pill (e.g. "12 projects") */
  meta?: ReactNode;
  /** Right-aligned page-level actions (1 primary max) */
  actions?: ReactNode;
}

interface PageBarEntry {
  id: string;
  config: PageBarConfig;
}

interface PageBarRegistry {
  register: (id: string, config: PageBarConfig) => void;
  unregister: (id: string) => void;
}

const PageBarRegistryContext = createContext<PageBarRegistry | null>(null);
const PageBarConfigContext = createContext<PageBarConfig | null>(null);

export function PageBarProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [entries, setEntries] = useState<PageBarEntry[]>([]);

  const registry = useMemo<PageBarRegistry>(
    () => ({
      register: (id: string, config: PageBarConfig) => {
        setEntries((prev) => {
          const index = prev.findIndex((entry) => entry.id === id);
          if (index === -1) return [...prev, { id, config }];
          const next = [...prev];
          next[index] = { id, config };
          return next;
        });
      },
      unregister: (id: string) => {
        setEntries((prev) => prev.filter((entry) => entry.id !== id));
      },
    }),
    [],
  );

  const activeConfig =
    entries.length > 0 ? entries[entries.length - 1].config : null;

  return (
    <PageBarRegistryContext.Provider value={registry}>
      <PageBarConfigContext.Provider value={activeConfig}>
        {children}
      </PageBarConfigContext.Provider>
    </PageBarRegistryContext.Provider>
  );
}

/**
 * Register this page's content in the shell PageBar.
 * Call once at the top level of a page component; content is cleared on
 * unmount so the next route starts blank.
 */
export function usePageBar(config: PageBarConfig): void {
  const id = useId();
  const registry = useContext(PageBarRegistryContext);
  const { title, crumb, description, meta, actions } = config;

  // Keep the registered config in sync with the latest render
  useEffect(() => {
    if (!registry) return;
    registry.register(id, { title, crumb, description, meta, actions });
  }, [registry, id, title, crumb, description, meta, actions]);

  // Unregister only on unmount
  useEffect(() => {
    if (!registry) return;
    return () => registry.unregister(id);
  }, [registry, id]);
}

/**
 * The page header block — mounted once by DashboardLayoutFull, below the
 * global strip + attention slot and above the scrollable content outlet.
 */
export function PageBar(): JSX.Element | null {
  const config = useContext(PageBarConfigContext);

  if (!config) return null;

  const { crumb, title, description, meta, actions } = config;

  return (
    <div
      data-testid="page-bar"
      className="flex-shrink-0 bg-transparent"
    >
      <div className="flex flex-col gap-3 border-b border-border/60 px-6 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5 sm:px-8 lg:px-10">
        <div className="min-w-0">
          {/* Back-crumb for detail pages — inline before the title. */}
          {crumb && (
            <div className="mb-1 flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
              <ChevronLeft className="h-4 w-4 shrink-0" />
              <span className="min-w-0 max-w-[40vw] truncate">{crumb}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {meta && (
              <span className="hidden shrink-0 items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground/90 sm:inline-flex">
                {meta}
              </span>
            )}
          </div>
          {description && (
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>

        {actions && (
          <div className="flex items-center gap-2 sm:shrink-0 sm:pt-0.5">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
